import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DEMAND } from '@demandproai/shared';
import Constants from '../Constants';
import { classifyPreviewStreamResult } from '../utils/previewStreamOutcome';
import { sanitizeLifecarePlanForPreview } from '../utils/lifecarePlanUtils';
import { toaster } from '../utils/toast';
import { PREVIEW_FAILURE, enrichPreviewFailureMessage } from '../utils/previewMessageUtils';
import useLifecareStudioData from './useLifecareStudioData';
import useLifecarePlanEditor from './useLifecarePlanEditor';
import useDemandStudioPreview from './useDemandStudioPreview';
import useDemandStudioTips from './useDemandStudioTips';
import useLifecareStudioSuggestedEdits from './useLifecareStudioSuggestedEdits';
import useLifecareStudioTabView from './useLifecareStudioTabView';
import useLifecareStudioApprove from './useLifecareStudioApprove';
import useSnapshotUndo from './useSnapshotUndo';
import useStudioRegeneration, { toastRegenerationResult, useSharedRegenerationOutcome } from './useStudioRegeneration';
import useSettledSignalEffect from './useSettledSignalEffect';

const REGEN_SUCCESS = 'Lifecare plan regenerated.';

// orchestrates the Formik-based lifecare studio: data fetch, autosave, preview
// pipeline, and the save-and-close action. Components only consume this hook.
const useLifecareStudio = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [staleViewerUrlSnapshot, setStaleViewerUrlSnapshot] = useState(null);
  const [isFinalizingExit, setIsFinalizingExit] = useState(false);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const {
    regenerate,
    isRegenerating
  } = useStudioRegeneration({
    caseId,
    endpoint: Constants.ApiUrl.regenerateLifecarePlan,
  });

  const { regenerateSilently, claimOutcomeHandling } = useSharedRegenerationOutcome(regenerate);

  // regenerate as fire-and-forget whenever the studio is left without an explicit save
  const regenerateRef = useRef(regenerateSilently);
  regenerateRef.current = regenerateSilently;
  useEffect(() => () => {
    regenerateRef.current();
  }, []);

  const dataState = useLifecareStudioData({ caseId, isOpen });
  const {
    caseDetails,
    caseName,
    clients,
    isInitialLoadPending,
    lifecareClientIdAndDisplayNames,
    selectedClientId,
    setSelectedClientId: setSelectedClientIdRaw,
    selectedClient,
    selectedPlan,
    resultRequests,
    hasMoreHistory,
    loadOlderUntil,
    refetchCaseDetails,
  } = dataState;

  const onManualEditSaved = useCallback(async ()=>{
    await Promise.all([
      regenerateSilently(),
      refetchCaseDetails()
    ]);
  }, [regenerateSilently, refetchCaseDetails]);

  // delegate per-client wiring (form + autosave + new-plan detection) to the
  // shared editor hook so the embedded demand-studio section can reuse it.
  const {
    formState,
    autosaveStatus,
    isNewPlan,
    flushAutosave,
    triggerAutosave,
    isSavingManualEdit
  } = useLifecarePlanEditor({
    caseId,
    client: selectedClient,
    plan: selectedPlan,
    onManualEditSaved
  });

  const { forceReinitOnNextUpdate } = formState || {};

  // a suggested edit rewrites the plan server side, so the agent's version wins over the
  // last autosaved values Formik still tracks as dirty -- without the arming the refetched
  // plan is dropped and the form keeps showing the pre-edit content until a page reload
  const onSuggestedEditApplied = useCallback(async () => {
    forceReinitOnNextUpdate?.();
    await refetchCaseDetails();
  }, [forceReinitOnNextUpdate, refetchCaseDetails]);

  // ensure any pending edits for the previous client land before swapping
  const setSelectedClientId = useCallback(
    async (nextClientId) => {
      if (nextClientId === selectedClientId) return;
      await flushAutosave();
      setSelectedClientIdRaw(nextClientId);
    },
    [selectedClientId, flushAutosave, setSelectedClientIdRaw]
  );

  const suggestedEditsState = useLifecareStudioSuggestedEdits({
    caseId,
    isOpen,
    selectedClientId,
    lifecareClientIdAndDisplayNames,
    onSuggestedEditApplied
  });

  const previewState = useDemandStudioPreview({
    caseId,
    isOpen: isOpen && showPreview,
    docxFiles: caseDetails?.docxFiles,
    clients,
    forcedDemandType: DEMAND.Lifecare_Plan,
    forcedClientId: selectedClientId,
    onStaleDocuments: regenerate,
    suggestedEditsSettledSignal: suggestedEditsState.suggestedEditsSettledSignal,
  });

  const refreshPreviewRef = useRef(null);
  refreshPreviewRef.current = previewState.refreshPreview;
  const viewerUrlRef = useRef('');
  viewerUrlRef.current = previewState.viewerUrl;

  // clear the stale mask once the regenerated PDF replaces the old viewerUrl
  useEffect(() => {
    if (staleViewerUrlSnapshot === null) return;
    const { viewerUrl } = previewState;
    if (viewerUrl && viewerUrl !== staleViewerUrlSnapshot) {
      setStaleViewerUrlSnapshot(null);
      setIsPreviewLoading(false);
    }
  }, [previewState, staleViewerUrlSnapshot]);

  const handleRegenerateSection = useCallback(
    async (sectionId) => {
      await flushAutosave();
      await suggestedEditsState.handleRegenerateSection(sectionId);
    },
    [flushAutosave, suggestedEditsState]
  );

  const suggestedEditsStateWithFlush = useMemo(
    () => ({ ...suggestedEditsState, handleRegenerateSection }),
    [suggestedEditsState, handleRegenerateSection]
  );

  const {
    handleUndo: handleUndoRaw,
    isUndoLoading,
    hasUndo,
    hasRedo
  } = useSnapshotUndo({
    caseId,
    fetchCaseDetails: refetchCaseDetails,
    resultRequests,
    clientId: selectedClientId,
    hasMoreHistory,
    loadOlderUntil,
    onEditPersisted: regenerateSilently,
  });

  const isUndoingRef = useRef(false);
  const [isUndoPreludeLoading, setIsUndoPreludeLoading] = useState(false);

  const handleUndo = useCallback(
    async (isRedo) => {
      if (isUndoingRef.current) return;
      isUndoingRef.current = true;
      setIsUndoPreludeLoading(true);
      try {
        await flushAutosave();
        forceReinitOnNextUpdate();
        await handleUndoRaw(isRedo);
      } finally {
        isUndoingRef.current = false;
        setIsUndoPreludeLoading(false);
      }
    }, [setIsUndoPreludeLoading, flushAutosave, forceReinitOnNextUpdate, handleUndoRaw]);

  const undoStateWithFlush = useMemo(() => ({
    handleUndo,
    isUndoLoading: isUndoLoading || isUndoPreludeLoading,
    isManualEditProcessing: isSavingManualEdit,
    hasUndo,
    hasRedo
  }),
    [
      handleUndo,
      isUndoLoading,
      isUndoPreludeLoading,
      isSavingManualEdit,
      hasUndo,
      hasRedo
    ]
  );

  const tabState = useLifecareStudioTabView({ plan: formState?.values });

  const tips = useDemandStudioTips();

  const approveState = useLifecareStudioApprove({
    caseId,
    selectedClientId,
    selectedClient,
    refetchCaseDetails,
  });

  // a settled poll (always SUGGESTED_EDIT) rearms the form so it reinitializes with the AI's result
  // instead of keeping the autosaved values Formik still tracks as dirty
  useSettledSignalEffect({
    signal: suggestedEditsState.suggestedEditsSettledSignal,
    caseId,
    isEnabled: isOpen,
    onSettled: onSuggestedEditApplied,
  });

  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // hold the user in the studio until any pending regeneration finishes, then return
  const handleSaveAndClose = useCallback(async () => {
    setIsFinalizingExit(true);
    try {
      await flushAutosave();
      const regenerationPromise = regenerate();
      claimOutcomeHandling(regenerationPromise);
      toastRegenerationResult(await regenerationPromise);
    } finally {
      setIsFinalizingExit(false);
      handleClose();
    }
  }, [flushAutosave, regenerate, handleClose]);

  const handleTogglePreview = useCallback(() => {
    setShowPreview((prev) => !prev);
  }, []);

  const setPreviewDoneLoading = useCallback(() => {
    setIsPreviewLoading(false);
    setStaleViewerUrlSnapshot(null);
  }, [setIsPreviewLoading, setStaleViewerUrlSnapshot]);

  const handlePreviewFailure = useCallback((result) => {
    toaster({ message: enrichPreviewFailureMessage(result?.message || PREVIEW_FAILURE), success: false });
    setPreviewDoneLoading();
  }, [setPreviewDoneLoading]);

  const handlePreviewUpToDate = useCallback(() => {
    setPreviewDoneLoading();
  }, [setPreviewDoneLoading]);

  const handlePreviewSuccess = useCallback(() => {
    toaster({ message: REGEN_SUCCESS, success: true });
    setIsPreviewLoading(false);
    // full regen finished — snapshot current URL so the stale mask shows until the new PDF loads
    setStaleViewerUrlSnapshot(viewerUrlRef.current || '');
    refetchCaseDetails();
    refreshPreviewRef.current?.();
  }, [refetchCaseDetails]);

  const applyPreviewStreamResult = useCallback((result) => {
    const outcomeHandlers = {
      failure: handlePreviewFailure,
      upToDate: handlePreviewUpToDate,
      success: handlePreviewSuccess,
    };
    outcomeHandlers[classifyPreviewStreamResult(result)](result);
  }, [handlePreviewFailure, handlePreviewUpToDate, handlePreviewSuccess]);

  const handlePreview = useCallback(async () => {
    if (!caseId) return;
    // open the preview right away so the skeleton and updating badge show instantly
    setShowPreview(true);
    setIsPreviewLoading(true);
    try {
      await flushAutosave();
      if (selectedClientId && formState?.values) {
        triggerAutosave({ plan: sanitizeLifecarePlanForPreview(formState.values) });
        await flushAutosave();
      }
      const regenerationPromise = regenerate();
      claimOutcomeHandling(regenerationPromise);
      const result = await regenerationPromise;
      applyPreviewStreamResult(result);
    } catch (error) {
      console.error('[lifecare preview] unexpected error', error);
      setPreviewDoneLoading();
      toaster({ message: error?.message || PREVIEW_FAILURE, success: false });
    }
  }, [caseId, flushAutosave, triggerAutosave, selectedClientId, formState, regenerate, claimOutcomeHandling, applyPreviewStreamResult, setPreviewDoneLoading]);

  const isPreviewStale = isRegenerating || staleViewerUrlSnapshot !== null;
  const hasAnyLifecareClient = lifecareClientIdAndDisplayNames.length > 0;

  return useMemo(
    () => ({
      caseId,
      caseDetails,
      caseName,
      forcedDemandType: DEMAND.Lifecare_Plan,
      forcedClientId: selectedClientId,
      isInitialLoadPending,
      showPreview,
      isPreviewLoading,
      isPreviewStale,
      hasAnyLifecareClient,
      isNewPlanForSelectedClient: isNewPlan,
      lifecareClientIdAndDisplayNames,
      selectedClientId,
      setSelectedClientId,
      selectedClient,
      autosaveStatus,
      formState,
      previewState,
      suggestedEditsState: suggestedEditsStateWithFlush,
      undoState: undoStateWithFlush,
      tabState,
      tips,
      approveState,
      isFinalizingExit,
      handleSaveAndClose,
      handleTogglePreview,
      handlePreview,
    }),
    [
      caseId,
      caseDetails,
      caseName,
      isInitialLoadPending,
      showPreview,
      isPreviewLoading,
      isPreviewStale,
      hasAnyLifecareClient,
      isNewPlan,
      lifecareClientIdAndDisplayNames,
      selectedClientId,
      setSelectedClientId,
      selectedClient,
      autosaveStatus,
      formState,
      previewState,
      suggestedEditsStateWithFlush,
      undoStateWithFlush,
      tabState,
      tips,
      approveState,
      isFinalizingExit,
      handleSaveAndClose,
      handleTogglePreview,
      handlePreview,
    ]
  );
};

export default useLifecareStudio;
